'use client';

import React from 'react';
import styled from '@emotion/styled';
import GenericPage from '../../../../app/pages/ui/GenericPage';
import { IPageEntry } from '../../../../../contentful/types/IPage';
import { ContentWrap, Css, Responsive, TableRole } from '../../../../../ui';
import { SolicitationProposersListHeader } from './SolicitationProposersListHeader';
import { PageRoutes } from '../../../../app/pages/domain/constants/PageRoutes';
import { SolicitationProposersListRow } from './SolicitationProposersListRow';
import { ISolicitationProposer } from '../../../domain/models/ISolicitationProposer';
import { SolicitationProposersListRowMobile } from './SolicitationProposersListRowMobile';
import { ISolicitationEntry } from '../../../../../contentful/types/ISolicitationEntry';
import { NoResults } from '../../../../../components/common/no-results/NoResults';
import { ErrorMessages } from '../../../../../utils/errorMessages';
import BackLink from '../../../../../components/common/button/BackLink';
import { useApi } from '../../../../../data/hooks/useApi';
import { SOLICITATION_API } from '../../../domain/api';
import SolicitationProposersListShimmer from './SolicitationProposersListShimmer';
import ErrorMessage from '../../../../shared/components/error/ErrorMessage';
import { ISiteSectionEntry } from '../../../../../contentful/types/ISiteSection';

interface ISolicitationProposersList {
	page: IPageEntry;
	solicitation: ISolicitationEntry;
	siteSection?: ISiteSectionEntry;
}

const API_KEY = process.env.HAS_SOLICITATIONS_API_KEY;

export default function SolicitationProposersList({
	page,
	solicitation,
	siteSection,
}: ISolicitationProposersList) {
	const isMobileXL = Responsive.useMatchesBreakpoint('mobileXL');
	const previousSlug = solicitation.fields.slug;
	const backLink = `${PageRoutes.CorporateSolicitationsDetails}/${previousSlug}`;

	const { data, loading, error } = useApi<ISolicitationProposer[]>().useGetApi({
		url: `${SOLICITATION_API}?contentful_id=${solicitation.id}`,
		headers: { 'x-api-key': `${API_KEY}` },
	});

	const noProposersFound = error && 'status' in error && error.status === 404;
	const showGenericError = error && 'status' in error && error.status !== 404;

	return (
		<GenericPage
			title="Prospective Proposers List"
			backlink={backLink}
			backlinkText="Project Details"
			page={page}
			hideHeaderImage
			siteSection={siteSection}
		>
			<ContentWrap>
				<StyledTableRole>
					<SolicitationProposersListHeader />

					{loading && <SolicitationProposersListShimmer />}

					{noProposersFound && (
						<NoResultsWrapper>
							<NoResults
								title="No Prospective Proposers yet"
								message={ErrorMessages.ProposersNoResults}
							/>
							<BackLink
								backLink={backLink}
								ariaLabel="Go back to the solicitation page"
							>
								Back to the solicitation
							</BackLink>
						</NoResultsWrapper>
					)}

					{showGenericError && (
						<ErrorMessage message={ErrorMessages.GenericError} />
					)}

					{data?.length &&
						data.length > 0 &&
						data.map((proposer, index) =>
							isMobileXL ? (
								<SolicitationProposersListRowMobile
									key={proposer.id}
									proposer={proposer}
									isLast={index === data.length - 1}
									isFirst={index === 0}
								/>
							) : (
								<SolicitationProposersListRow
									key={proposer.id}
									proposer={proposer}
								/>
							),
						)}
				</StyledTableRole>
			</ContentWrap>
		</GenericPage>
	);
}

const NoResultsWrapper = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	margin-top: ${Css.pxToRem(20)};
	margin-bottom: ${Css.pxToRem(200)};

	.no-results {
		max-width: 360px;
		padding-bottom: ${Css.pxToRem(16)};
	}

	p {
		padding: 0 ${Css.pxToRem(30)};
	}
`;

const StyledTableRole = styled(TableRole)`
	min-height: 100vh;
`;
